import axios from "axios";
import { ApiResponse } from "@/types";
import { emojiToUnicode } from "@/constants/reaction-unicode";
import { logger } from "@/services/LoggerService";

export interface AddReactionParams {
    userId: string;
    reactableType: string;
    reactableId: string;
    reaction: string; // emoji
}

export interface RemoveReactionParams extends AddReactionParams {}

export type ReactionCountsResponse = Record<string, number>; // unicode -> count

export interface UserReactionItem {
    reactableId: string;
    reactionType: string; // unicode
}

export interface PaginatedReactions {
    data: Array<{ userId: string; reactionType: string; createdAt: string }>;
    meta?: { page: number; limit: number; total: number; pages: number };
}

export const addReaction = async ({
    userId,
    reactableType,
    reactableId,
    reaction,
}: AddReactionParams): Promise<ApiResponse<any>> => {
    try {
        const response = await axios.post("/api/reactions", {
            userId,
            reactableType,
            reactionType: emojiToUnicode(reaction),
            reactableId,
        });
        return response.data;
    } catch (error: any) {
        logger.error("Failed to add reaction", error, {
            userId,
            reactableType,
            reactableId,
            reaction,
            context: "ReactionService",
        });
        if (error.response?.data?.error) {
            throw new Error(error.response.data.error);
        }
        throw new Error(error.response?.data?.message || "Failed to add reaction");
    }
};

export const removeReaction = async ({
    userId,
    reactableType,
    reactableId,
    reaction,
}: RemoveReactionParams): Promise<ApiResponse<any>> => {
    try {
        const response = await axios.delete("/api/reactions", {
            data: {
                userId,
                reactableType,
                reactionType: emojiToUnicode(reaction),
                reactableId,
            },
        });
        return response.data;
    } catch (error: any) {
        logger.error("Failed to remove reaction", error, {
            userId,
            reactableType,
            reactableId,
            reaction,
            context: "ReactionService",
        });
        if (error.response?.data?.error) {
            throw new Error(error.response.data.error);
        }
        throw new Error(error.response?.data?.message || "Failed to remove reaction");
    }
};

export const getReactionCounts = async (
    reactableType: string,
    reactableId: string,
): Promise<ApiResponse<ReactionCountsResponse>> => {
    try {
        const response = await axios.get(`/api/reactions/${reactableType}/${reactableId}/count`);
        return response.data;
    } catch (error: any) {
        logger.error("Failed to fetch reaction counts", error, {
            reactableType,
            reactableId,
            context: "ReactionService",
        });
        throw new Error(error.response?.data?.message || "Failed to fetch reaction counts");
    }
};

export async function getUserReactionsForProperty(
    userId: string,
    propertyId: string,
    order: "ASC" | "DESC" = "ASC",
): Promise<ApiResponse<UserReactionItem[]>> {
    const response = await axios.get(`/api/reactions/${userId}/listing`, {
        params: {
            propertyId,
            order,
        },
    });
    return response.data;
}

export const getListingReactions = async (
    reactableType: string,
    reactableId: string,
    reactionType: string | null = null,
    page: number = 1,
    limit: number = 10,
): Promise<ApiResponse<PaginatedReactions>> => {
    try {
        const params: Record<string, any> = { page, limit };
        if (reactionType) {
            params.reactionType = reactionType;
        }

        const response = await axios.get(`/api/reactions/${reactableType}/${reactableId}/users`, { params });
        return response;
    } catch (error: any) {
        logger.error("Failed to fetch listing reactions", error, {
            reactableType,
            reactableId,
            reactionType,
            page,
            limit,
            context: "ReactionService",
        });
        throw new Error(error.response?.data?.message || "Failed to fetch listing reactions");
    }
};
